import React, { useRef, useState } from 'react';

import {
  IconBattery,
  IconCrashedCar,
  IconFlatTire,
  IconFuelBomb,
  IconKey,
  IconWinchTruck,
} from '@assets/icons';
import {
  ButtonMain,
  InputCheckBox,
  InputMask,
  InputSelect,
  Paragraph,
  SubtitleSecondary,
} from '@components/index';
import { ConfigStorage } from '@config/index';
import { newProviderStorageService } from '@services/index';
import { toastify } from '@utils/notifiers';

import { hour_types_select, services, daysOfWeek } from './data';
import {
  schemaHours,
  schemaSelectedHourType,
  schemaServices,
} from './schemaValidation';

import {
  Container,
  FormProviderService,
  Sections,
  SectionsGroup,
  SectionsItem,
  ServiceImage,
} from './styles';

interface IProps {
  forNewProviderService?: {
    advanceStep(): void;
  };
}

const FormProviderNewService: React.FC<IProps> = ({
  forNewProviderService,
}) => {
  const formRef = useRef(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedHourType, setSelectedHourType] = useState<string>('');

  const [openingHours, setOpeningHours] = useState<any>({
    util_day: '',
    saturday: '',
    sunday: '',
  });
  const [avaliableServices, setAvaliableServices] = useState<any>({
    reboque_car: {
      text: '',
      value: false,
    },
    reboque_moto: {
      text: '',
      value: false,
    },
    reboque_suv: {
      text: '',
      value: false,
    },
    drypan: {
      text: '',
      value: false,
    },
    keychain: {
      text: '',
      value: false,
    },
    battery_recharge: {
      text: '',
      value: false,
    },
    tire_change: {
      text: '',
      value: false,
    },
  });

  const handleOpeningHours = (name: string, value: string) => {
    setOpeningHours((prevHours: any) => ({
      ...prevHours,
      [name]: value,
    }));
  };

  const handleChecked = (name: string, checked: boolean, innerText: string) => {
    setAvaliableServices((prevServices: any) => ({
      ...prevServices,
      [name]: {
        value: checked,
        text: checked ? innerText : '',
      },
    }));
  };

  const handleSubmitServices = async () => {
    try {
      setIsLoading(prevLoading => !prevLoading);
      await schemaServices.validate(avaliableServices, { abortEarly: false });
      await schemaSelectedHourType.validate(selectedHourType, {
        abortEarly: false,
      });
      if (selectedHourType === 'fixed_hour') {
        await schemaHours.validate(openingHours, { abortEarly: false });
      }

      const selectedServices = Object.entries(avaliableServices)
        .map((service: [string, any]) => ({
          label: [service[0]][0],
          value: service[1].value,
          text: service[1].text,
        }))
        .filter(selectedService => selectedService.value);

      const servicesData = {
        selectedHourType,
        selectedServices,
        openingHours,
      };

      newProviderStorageService.updateServices(servicesData);

      setIsLoading(prevLoading => !prevLoading);
      forNewProviderService?.advanceStep();
    } catch (error: any) {
      setIsLoading(prevLoading => !prevLoading);
      toastify(error.message, 'error');
    }
  };

  return (
    <Container>
      <FormProviderService
        ref={formRef}
        initialData={[]}
        onSubmit={handleSubmitServices}
      >
        <Sections>
          <SectionsGroup>
            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                fontSize={16}
                nameColor="black"
              >
                Serviços
              </SubtitleSecondary>
              <Paragraph
                textAlign="start"
                style={{ marginBottom: '2vh' }}
                nameColor="black"
                opacity={0.5}
              >
                Selecione um ou mais serviços
              </Paragraph>
              {services.map(service => (
                <div
                  key={service.id}
                  style={{
                    display: 'flex',
                    padding: '1vw 0 0 0',
                    gap: '2vw',
                    alignItems: 'center',
                  }}
                >
                  {service.code === 'ser-71595200104160' && (
                    <ServiceImage>
                      <IconFuelBomb />
                    </ServiceImage>
                  )}
                  {service.code === 'ser-14007219464468' && (
                    <ServiceImage>
                      <IconCrashedCar />
                    </ServiceImage>
                  )}
                  {service.code === 'ser-22771391723840' && (
                    <ServiceImage>
                      <IconFlatTire />
                    </ServiceImage>
                  )}
                  {service.code === 'ser-49183601941352' && (
                    <ServiceImage>
                      <IconBattery />
                    </ServiceImage>
                  )}
                  {service.code === 'ser-58133848228926' && (
                    <ServiceImage>
                      <IconKey />
                    </ServiceImage>
                  )}
                  {service.code === 'ser-77362873760431' && (
                    <ServiceImage>
                      <IconWinchTruck />
                    </ServiceImage>
                  )}
                  <InputCheckBox
                    key={service.id}
                    label={service.label}
                    name={service.name}
                    id={service.label}
                    onChange={({ target }) => {
                      handleChecked(target.name, target.checked, target.id);
                    }}
                    defaultChecked={false}
                  />
                </div>
              ))}
            </SectionsItem>

            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                fontSize={16}
                nameColor="black"
              >
                Horário de funcionamento
              </SubtitleSecondary>
              <Paragraph
                textAlign="start"
                style={{ marginBottom: '2vh' }}
                nameColor="black"
                opacity={0.5}
              >
                Selecione qual o tipo de horário de funcionamento
              </Paragraph>
              <InputSelect
                placeholder="Selecione um tipo de horário"
                name="hour_type"
                options={hour_types_select}
                onChange={({ target }) => {
                  setSelectedHourType(target.value);
                  if (target.value === '24_hour') {
                    setOpeningHours({
                      util_day: '',
                      saturday: '',
                      sunday: '',
                    });
                  }
                }}
              />
              {selectedHourType === 'fixed_hour' &&
                daysOfWeek.map(day => (
                  <>
                    <Paragraph
                      textAlign="start"
                      nameColor="black"
                      style={{ margin: '2vh 0 0 0' }}
                    >
                      {day.label}
                    </Paragraph>
                    <InputMask
                      name={day.value}
                      mask="99h-99h"
                      onChange={({ target }) => {
                        handleOpeningHours(target.name, target.value);
                      }}
                      placeholder="Horário"
                    />
                  </>
                ))}
            </SectionsItem>
          </SectionsGroup>
          <ButtonMain
            type="submit"
            loading={isLoading}
            style={{ marginTop: '4vh', maxWidth: 250 }}
          >
            Continuar
          </ButtonMain>
        </Sections>
      </FormProviderService>
    </Container>
  );
};

export default FormProviderNewService;
